import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateRole, useGetRole, useUpdateRole } from '../../hooks/api/use-roles.hook';
import { RoleApiModel } from '../../../../types/models/role.model';
import { LoadingIndicator } from '../LoadingIndicator';
import { Permission } from '../../../../types/constants/permission';
import { RoleForm } from './RoleForm';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditRole = () => {
  const navigate = useNavigate();
  const { roleId } = useParams<{ roleId: string }>();

  const { role, loading: loadingRole, error: gettingError } = useGetRole<RoleApiModel>({ roleId: Number(roleId) });
  const [createRole, { loading: creatingRole, error: createError }] = useCreateRole();
  const [updateRole, { loading: updatingRole, error: updateError }] = useUpdateRole();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<RoleForm>({ resolver: classValidatorResolver(RoleForm), values: role });

  const onSubmit: SubmitHandler<RoleForm> = async (submittedValues) => {
    const updatedValues = getDirtyValues(dirtyFields, submittedValues);
    await (role
      ? updateRole({ ...updatedValues, roleId: role.id })
      : createRole({ ...submittedValues, permissions: submittedValues.permissions ?? [] }));
    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    return (
      <>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingRole || updatingRole}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <Controller
          control={control}
          name="permissions"
          render={({ field }) => (
            <Autocomplete
              {...register('permissions')}
              loading={loadingRole}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              options={Object.values(Permission)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Permissions"
                  error={Boolean(errors.permissions)}
                  helperText={errors.permissions?.message}
                />
              )}
            />
          )}
        />
      </>
    );
  };

  if (loadingRole) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingRole ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${role ? 'Edit' : 'Add'} Role`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {role ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
